// ============================================================
// IoTIntro.jsx
//
// Course content page for "Introduction to IoT" — the first
// lesson in the IoT track. Rewritten to:
//   1) fix a mismatch where the header comment documented the
//      background as #0B0E14 but the code actually used #233D4D,
//   2) drop a reference to the 'Orbitron' font, which was never
//      loaded anywhere in the app, so it silently did nothing,
//   3) remove two unused imports (Tooltip, Collapse),
//   4) match the app-wide dark-purple/goldenrod look used in
//      IoTDashboard.jsx and SensorsActuators.jsx,
//   5) use arrow-function components and Material Icons only,
//   6) add error handling around the clipboard copy action.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Avatar,
    IconButton,
    Stack,
} from "@mui/material";

// ── Icons — @mui/icons-material only, matching the rest of the app ──
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SensorsIcon from "@mui/icons-material/Sensors";
import RouterIcon from "@mui/icons-material/Router";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import MemoryIcon from "@mui/icons-material/Memory";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import SecurityIcon from "@mui/icons-material/Security";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TerminalIcon from "@mui/icons-material/Terminal";
import DoneIcon from "@mui/icons-material/Done";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

// Sub-components (arrow-function style, defined before first use)

const SectionTitle = ({ icon, label }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box
            sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${BRAND.gold}1a, transparent)`,
                border: `1px solid ${BRAND.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            {React.cloneElement(icon, { style: { color: BRAND.gold, fontSize: 20 } })}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND.white, letterSpacing: 0.5 }}>
            {label}
        </Typography>
    </Box>
);

const ConceptCard = ({ icon, title, description }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            height: "100%",
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "all 0.3s ease",
            "&:hover": { borderColor: BRAND.gold, transform: "translateY(-5px)" },
        }}
    >
        <Box sx={{ mb: 2 }}>
            {React.cloneElement(icon, { style: { color: BRAND.gold, fontSize: 28 } })}
        </Box>
        <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700, mb: 1, fontSize: 16 }}>
            {title}
        </Typography>
        <Typography sx={{ color: BRAND.muted, fontSize: 14, lineHeight: 1.6 }}>{description}</Typography>
    </Paper>
);

const CodeBlock = ({ code, label = "Source Code" }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context —
            // fail silently rather than breaking the page.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 3, overflow: "hidden", mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontFamily: "monospace", fontWeight: 700 }}>
                    {label}
                </Typography>
                <IconButton onClick={handleCopy} size="small" sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#FCD34D", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const IoTIntro = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 8, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ textAlign: "center", mb: 10 }}>
                <Chip
                    label="Module 01: Connectivity Foundations"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.gold}`, mb: 3, fontWeight: 700, px: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 3 }}>
                    Introduction to <span style={{ color: BRAND.teal }}>IoT</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, mx: "auto", lineHeight: 1.8 }}>
                    The Internet of Things (IoT) bridges the gap between the physical and digital worlds.
                    Learn how sensors, microcontrollers, and cloud networks converge to create intelligent,
                    autonomous systems.
                </Typography>
            </Box>

            {/* WHAT IS IOT? */}
            <Box sx={{ mb: 8 }}>
                <SectionTitle icon={<LightbulbIcon />} label="The Connected Ecosystem" />
                <Typography sx={{ color: BRAND.muted, fontSize: 16, lineHeight: 1.9, mb: 4 }}>
                    IoT is not just about "smart" gadgets. It is a massive network of physical objects embedded
                    with sensors, software, and technologies for the purpose of connecting and exchanging data
                    with other devices over the internet. From <strong>precision agriculture</strong> to{" "}
                    <strong>remote patient monitoring</strong>, IoT is the nervous system of the modern world.
                </Typography>

                <Grid container spacing={3}>
                    {[
                        { icon: <SensorsIcon />, title: "Perception Layer", description: "The physical 'senses' — sensors and actuators that collect data from the environment (temperature, light, motion)." },
                        { icon: <RouterIcon />, title: "Network Layer", description: "The communication pathways like Wi-Fi, LoRaWAN, and cellular that transmit data from the device to the cloud." },
                        { icon: <CloudUploadIcon />, title: "Application Layer", description: "Where data is processed. This includes cloud dashboards, AI analysis, and user-facing mobile apps." },
                    ].map((item) => (
                        <Grid item xs={12} md={4} key={item.title}>
                            <ConceptCard {...item} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 8 }} />

            {/* THE CORE PILLARS */}
            <Box sx={{ mb: 8 }}>
                <SectionTitle icon={<SettingsInputComponentIcon />} label="Key Learning Outcomes" />
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ p: 4, borderRadius: 4, background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="h6" sx={{ color: BRAND.gold, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                <MemoryIcon /> Hardware Mastery
                            </Typography>
                            <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 15 }}>
                                You will learn to program microcontrollers like the ESP32 and Arduino,
                                interfacing them with peripherals using I2C and SPI protocols.
                            </Typography>
                            <CodeBlock
                                label="Arduino Blink Snippet"
                                code={`void setup() {\n  pinMode(LED_BUILTIN, OUTPUT);\n}\n\nvoid loop() {\n  digitalWrite(LED_BUILTIN, HIGH);\n  delay(1000);\n  digitalWrite(LED_BUILTIN, LOW);\n  delay(1000);\n}`}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            {[
                                { icon: <RssFeedIcon />, title: "Wireless Protocols", desc: "Understand MQTT, HTTP, and CoAP for efficient data transfer." },
                                { icon: <SecurityIcon />, title: "IoT Security", desc: "Learn to secure edge devices against modern cyber threats." },
                                { icon: <ShowChartIcon />, title: "Data Analytics", desc: "Visualize real-time streams using tools like Grafana or Adafruit IO." },
                            ].map((step) => (
                                <Box key={step.title} sx={{ display: "flex", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218,165,32,0.1)", border: `1px solid ${BRAND.gold}`, color: BRAND.gold }}>
                                        {step.icon}
                                    </Avatar>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700 }}>{step.title}</Typography>
                                        <Typography variant="body2" sx={{ color: BRAND.muted }}>{step.desc}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                </Grid>
            </Box>

            {/* SUMMARY CHECKLIST */}
            <Box sx={{ mb: 8, p: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <SectionTitle icon={<CheckCircleOutlineIcon />} label="What You'll Build" />
                <Grid container spacing={2}>
                    {[
                        "Smart weather stations with cloud logging",
                        "Home automation systems using MQTT brokers",
                        "Wearable health trackers with Bluetooth",
                        "Industrial monitoring systems with failure prediction",
                        "Secure OTA (Over-the-Air) firmware update logic",
                    ].map((text) => (
                        <Grid item xs={12} sm={6} key={text}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <DoneIcon sx={{ color: BRAND.teal, fontSize: 18 }} />
                                <Typography sx={{ color: BRAND.muted, fontSize: 14 }}>{text}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CALL TO ACTION */}
            <Box sx={{ textAlign: "center", p: 6, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to connect the world?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Grab your ESP32 and let's start building.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Explore Documentation" component="a" href="#" clickable sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 900 }} />
                    <Chip label="Join IoT Discord" component="a" href="#" clickable variant="outlined" sx={{ borderColor: BRAND.gold, color: BRAND.gold }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default IoTIntro;
